// 引入axios插件
import axios from "axios";
import { ElMessage } from "element-plus";
import fingerprintjs from "@fingerprintjs/fingerprintjs";
import { getToken } from "@/utils/utils.js"
async function getDeviceFingerprint() {
  let fp = await fingerprintjs.load();
  let result = await fp.get();
  let browserId = result.visitorId;
  localStorage.setItem("browserId", browserId);
  return browserId;
}
getDeviceFingerprint();

// 不全局设置 Content-Type，避免跨域 GET/下载触发预检

// 创建axios实例
const myAxios = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL,
  timeout: 300000,
});

// 请求拦截器
myAxios.interceptors.request.use(
  async (config) => {
    // console.log("config", config);
    // 在这里统一添加请求头，就不用每次调用接口的每次都手写了。如果token存在，那么添加token请求头。
    const isBlob = config.responseType === "blob";
    const urlString = config.url || "";
    let isCrossOriginBlob = false;
    try {
      const target = new URL(urlString, window.location.href);
      isCrossOriginBlob = isBlob && target.origin !== window.location.origin;
    } catch (e) {
      // ignore URL parse errors
    }

    // 对于跨域的文件直链下载（如 OSS/CDN），避免附加自定义请求头，防止触发 CORS 预检导致偶发失败
    if (!isCrossOriginBlob) {
      if (getToken()) {
        config.headers.token = getToken();
      }
      if (localStorage.getItem("browserId")) {
        config.headers.fingerprint = localStorage.getItem("browserId");
      } else {
        config.headers.fingerprint = await getDeviceFingerprint();
      }
    } else {
      // alert('非简单1')
      // 确保不携带任何会触发预检的非简单请求头
      if (config.headers && "token" in config.headers) delete config.headers.token;
      if (config.headers && "fingerprint" in config.headers) delete config.headers.fingerprint;
      // 明确不携带凭据
      config.withCredentials = false;
    }

    // GET 或 blob 下载请求不设置 Content-Type（避免预检）
    const methodUpper = (config.method || "").toUpperCase();
    if (isBlob || methodUpper === "GET") {
      // alert('非简单2')
      if (config.headers && "Content-Type" in config.headers) delete config.headers["Content-Type"];
      if (config.headers && "content-type" in config.headers) delete config.headers["content-type"];
    } else {
      // 仅在有请求体时设置 Content-Type
      if (config.data && !(config.data instanceof FormData)) {
        config.headers["Content-Type"] = "application/json;charset=utf-8";
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
myAxios.interceptors.response.use(
  (res) => {
    // 如果是文件，那么不需要判断code状态码，直接返回即可
    if (res.request.responseType === "blob") {
      const contentType = (res.headers && (res.headers["content-type"] || res.headers["Content-Type"])) || "";
      // 有些服务在报错时依然返回 blob，但实际内容是 JSON，需要解析后提示
      if (typeof contentType === "string" && contentType.includes("application/json")) {
        return new Promise((_, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const json = JSON.parse(String(reader.result || "{}"));
              ElMessage({
                message: json.msg || json.message || "文件下载失败",
                type: "error",
                customClass: "my-el-message-box",
              });
              reject(json);
            } catch (e) {
              ElMessage({
                message: "文件下载失败（服务端返回异常格式）",
                type: "error",
                customClass: "my-el-message-box",
              });
              reject(e);
            }
          };
          reader.onerror = () => {
            reject(new Error("无法读取错误响应"));
          };
          reader.readAsText(res.data);
        });
      }
      // 直接返回二进制
      return res.data;
    }

    // 如果接口报错，那么在这里统一提示错误信息弹窗，并且抛出异常；如果接口正确，那么返回数据。
    if (res.data.code !== 200) {
      // 错误弹窗
      ElMessage({
        message: res.data.msg || "默认错误提示信息",
        type: "error",
        customClass: "my-el-message-box",
      });
      // 如果用户没有登录
      if (res.data.code === -1) {
        // 清空本地存储
        localStorage.clear();
        // 跳转到登录页面
        window.location.href = "/#/login/nowLogin";
      }
      // 抛出异常
      return Promise.reject(res.data);
    } else {
      // 返回数据
      return res.data;
    }
  },
  async (error) => {
    const config = error && error.config ? error.config : {};
    const method = (config.method || "").toUpperCase();
    const url = config.url || "";
    const code = error && error.code ? error.code : undefined;

    // 统一格式化错误信息
    let messageText = "默认错误提示信息";
    if (error && error.response) {
      const { status, statusText, data } = error.response;
      const apiMsg = (data && (data.msg || data.message)) || undefined;
      messageText = `[${status}] ${statusText || "请求失败"} - ${method} ${url}` + (apiMsg ? `\n${apiMsg}` : "");
    } else if (code === "ECONNABORTED" || code === "ERR_NETWORK" || (error && /Network Error/i.test(String(error.message || "")))) {
      messageText = `${code || "网络错误"} - ${method} ${url}`;
      return Promise.reject(error);
    } else if (error && error.message) {
      messageText = `${error.message} - ${method} ${url}`;
    }

    ElMessage({
      message: messageText,
      type: "error",
      customClass: "my-el-message-box",
    });

    // 对部分瞬时错误做一次重试（最多1次）
    const shouldRetry =
      !config.__retryCount &&
      (
        code === "ERR_NETWORK" ||
        code === "ECONNABORTED" ||
        (error && error.response && [502, 503, 504].includes(error.response.status))
      );

    if (shouldRetry) {
      config.__retryCount = 1;
      // 指数退避的第一步，延迟 500ms
      await new Promise((r) => setTimeout(r, 500));
      return myAxios(config);
    }

    // 抛出异常
    return Promise.reject(error);
  }
);

export default myAxios;
